import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import html2canvas from "html2canvas";
import { selectAdminToken } from "../../admin/sessionSlice";
import { fetchThemeData } from "../../admin/themeSlice";
import { setBackButtonUrl } from "../uiSlice";
import { apiUrl } from "../../utils/apiUrl";
import { addReportToServer } from "../../functions/sendReport";
import { isVotingOpen } from "../../utils/yogaTheme";
import { resolveGameIconUrl, formatGameSubtitle, PENDING_APPROVAL_MESSAGE, badgeImageUrl as badgeImageProxyUrl } from "../../utils/gameHelpers";
import { themeButtonBackground, themeButtonTextColor } from "../../utils/colorUtils";
import RulesModal from "../games/shared/RulesModal";
import likeThumb from "../../img/popup-like.png";
import ohnoImg from "../../img/ohno.png";
import "./game.css";

const MESSAGE_MAX_LEN = 200;
const POSTS_PER_PAGE = 8;
const ALL_FILTER = "ALL";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Prefer public S3 for display; proxy kept for html2canvas download (CORS).
const mediaUrl = (file) => resolveGameIconUrl(file);

const isTrue = (v) => String(v ?? "").trim().toLowerCase() === "true";
const firstEntry = (obj) => Object.entries(obj || {})[0] || [];
const objArrToMap = (arr) =>
  (Array.isArray(arr) ? arr : []).reduce((acc, o) => {
    const [k, v] = firstEntry(o);
    if (k != null) acc[k] = v;
    return acc;
  }, {});

const normalizeKey = (name) =>
  String(name || "").trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");

function buildCategories(themeData) {
  const names = Array.isArray(themeData?.default_category_name) ? themeData.default_category_name : [];
  const icons = Array.isArray(themeData?.category_icons) ? themeData.category_icons : [];
  const descMap = objArrToMap(themeData?.category_placeholder_text);
  const msgMap = objArrToMap(themeData?.category_message_text);

  return names
    .map((name, i) => {
      const [iconKey, iconVal] = firstEntry(icons[i]);
      const key = iconKey || normalizeKey(name);
      return {
        name: String(name || ""),
        key,
        icon: String(iconVal || ""),
        description: String(descMap[key] ?? ""),
        message: String(msgMap[key] ?? ""),
      };
    })
    .filter((c) => c.name && c.name.toUpperCase() !== ALL_FILTER);
}

export default function GamePage() {
  const dispatch = useDispatch();
  const { data: themeData, status, error: themeError } = useSelector((s) => s.theme);
  const user = useSelector((s) => s.auth.user);
  const adminToken = useSelector(selectAdminToken);

  const categories = useMemo(() => buildCategories(themeData), [themeData]);

  const descOnHover = isTrue(themeData?.category_description);
  const useCategoryMessage = isTrue(themeData?.category_message_status);
  const additionalOn = isTrue(themeData?.additional_fields);
  const badgeSharingOn = isTrue(themeData?.badges_share_status ?? "true");
  const defaultMsg = String(themeData?.deafult_message_text ?? "");
  const votingOpen = isVotingOpen(themeData);

  const extraFieldDefs = useMemo(() => {
    if (!additionalOn) return [];
    const list = Array.isArray(themeData?.additional_new_fields) ? themeData.additional_new_fields : [];
    return list.filter((f) => f && f.selected);
  }, [additionalOn, themeData]);

  const labels = {
    name: themeData?.lable_name_text || "Your Name",
    colleagueName: themeData?.lable_colleague_name_text || "Your Colleagues name",
    colleagueEmail: themeData?.lable_colleague_email_text || "Your Colleagues email id",
    badge: themeData?.lable_badge_text || "Select a Badge",
  };

  const titleColor = themeData?.text_color || "#1a2b6d";
  const likeBoxText = themeData?.like_box_text_color || "#ffffff";
  const likeBoxBg = themeData?.like_box_background_color || "#e25569";
  const btnBg = themeButtonBackground(themeData) || "#088e01";
  const btnText = themeButtonTextColor(themeData) || "#ffffff";
  const btnStyle = {
    ...(themeData?.button_backgroundcolor && { backgroundColor: themeData.button_backgroundcolor }),
    ...(themeData?.button_color && { color: themeData.button_color }),
  };

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [name, setName] = useState("");
  const [colleagueName, setColleagueName] = useState("");
  const [colleagueEmail, setColleagueEmail] = useState("");
  const [message, setMessage] = useState("");
  const [extraValues, setExtraValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [zoomCat, setZoomCat] = useState(null);
  const [rulesOpen, setRulesOpen] = useState(false);

  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [likingId, setLikingId] = useState(null);
  const [showLikePopup, setShowLikePopup] = useState(false);
  const [showAlreadyLiked, setShowAlreadyLiked] = useState(false);
  const [activeFilter, setActiveFilter] = useState(ALL_FILTER);
  const [page, setPage] = useState(1);
  const [mainEl, setMainEl] = useState(null);
  const cardRefs = useRef({});
  const trackRef = useRef(null);
  const reportIdRef = useRef(null);

  // Render the Rules button into the full-width layout container so it aligns
  // to the page's top-right instead of the centered .cg-page box.
  useEffect(() => {
    setMainEl(document.querySelector(".ddd-user-main"));
  }, []);

  const carouselPauseUntilRef = useRef(0);

  const scrollCarousel = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const step = el.clientWidth;
    const max = Math.max(0, el.scrollWidth - el.clientWidth);
    if (max <= 4) return;
    let next;
    if (dir > 0 && el.scrollLeft >= max - 4) next = 0;
    else if (dir < 0 && el.scrollLeft <= 4) next = max;
    else next = Math.min(max, Math.max(0, el.scrollLeft + dir * step));
    el.scrollTo({ left: next, behavior: "smooth" });
  };

  const goCarousel = (dir) => {
    carouselPauseUntilRef.current = Date.now() + 7000;
    scrollCarousel(dir);
  };

  useEffect(() => {
    if (categories.length <= 2) return undefined;
    const id = setInterval(() => {
      if (Date.now() < carouselPauseUntilRef.current) return;
      scrollCarousel(1);
    }, 3500);
    return () => clearInterval(id);
  }, [categories.length]);

  const selectedCategory = categories[selectedIndex] || null;

  useEffect(() => {
    dispatch(setBackButtonUrl(user?.backButtonRedirect || "/login?&save=true"));
  }, [dispatch, user?.backButtonRedirect]);

  useEffect(() => {
    if (user?.name) setName((prev) => prev || user.name);
  }, [user?.name]);

  // Reset the message to the configured default whenever the selected badge changes.
  useEffect(() => {
    if (!selectedCategory) return;
    const next = useCategoryMessage ? selectedCategory.message || defaultMsg : defaultMsg;
    setMessage(String(next || "").slice(0, MESSAGE_MAX_LEN));
  }, [selectedIndex, useCategoryMessage, defaultMsg, selectedCategory]);

  const filterOptions = useMemo(() => {
    const names = categories.map((c) => c.name).filter((n) => n && n.toUpperCase() !== ALL_FILTER);
    return [ALL_FILTER, ...names];
  }, [categories]);

  const filteredPosts = useMemo(
    () => (activeFilter === ALL_FILTER ? posts : posts.filter((p) => p.category === activeFilter)),
    [posts, activeFilter]
  );

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / POSTS_PER_PAGE));
  const pagedPosts = useMemo(
    () => filteredPosts.slice((page - 1) * POSTS_PER_PAGE, page * POSTS_PER_PAGE),
    [filteredPosts, page]
  );

  // Keep the current page within range when the filter or post list changes.
  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  useEffect(() => {
    setPage(1);
  }, [activeFilter]);

  const loadPosts = useCallback(async () => {
    if (!adminToken) {
      setLoadingPosts(false);
      return;
    }
    setLoadingPosts(true);
    try {
      const resp = await fetch(apiUrl("/badgeGallery"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ userId: user?.userId || "" }),
      });
      const payload = await resp.json();
      setPosts(resp.ok && payload?.success && Array.isArray(payload.posts) ? payload.posts : []);
    } catch (e) {
      console.error("badgeGallery", e);
      setPosts([]);
    } finally {
      setLoadingPosts(false);
    }
  }, [adminToken, user?.userId]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const handleSubmit = async () => {
    if (!badgeSharingOn) {
      Swal.fire("Sharing disabled", "Badge sharing is currently disabled.", "info");
      return;
    }
    if (!user || !adminToken) {
      Swal.fire("Not signed in", "Please reload and sign in again.", "warning");
      return;
    }
    if (!selectedCategory) {
      Swal.fire("Select a badge", "Please choose a badge first.", "warning");
      return;
    }
    if (!name.trim()) {
      Swal.fire("Name required", `Please enter ${labels.name.toLowerCase()}.`, "warning");
      return;
    }
    if (!colleagueName.trim()) {
      Swal.fire("Colleague required", `Please enter ${labels.colleagueName.toLowerCase()}.`, "warning");
      return;
    }
    const email = colleagueEmail.trim();
    if (!email) {
      Swal.fire(
        "Email required",
        `Please enter ${labels.colleagueEmail.toLowerCase()}.`,
        "warning"
      );
      return;
    }
    if (!EMAIL_RE.test(email)) {
      Swal.fire(
        "Invalid email",
        `Please enter a valid ${labels.colleagueEmail.toLowerCase()} (must include @ and .).`,
        "warning"
      );
      return;
    }
    for (const f of extraFieldDefs) {
      if (!String(extraValues[f.name] ?? "").trim()) {
        const fieldLabel =
          f.label ||
          String(f.name || "")
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());
        Swal.fire("Missing field", `Please fill in "${fieldLabel}".`, "warning");
        return;
      }
    }

    setSubmitting(true);
    try {
      const resp = await fetch(apiUrl("/badgeSubmit"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          userId: user.userId,
          name: name.trim(),
          email: user.email || "",
          colleagueName: colleagueName.trim(),
          colleagueEmail: email,
          category: selectedCategory.name,
          message: message.trim(),
          icon: selectedCategory.icon,
          extraFields: extraFieldDefs.length ? extraValues : null,
          gameId: user.gameId || "",
          role: user.role || user.source || "",
          source: user.source || "",
        }),
      });
      const payload = await resp.json();
      if (!resp.ok || !payload?.success) {
        Swal.fire(
          payload?.limitReached ? "Limit reached" : "Could not share",
          payload?.error || "Something went wrong.",
          payload?.limitReached ? "info" : "error"
        );
        setSubmitting(false);
        return;
      }
      setColleagueName("");
      setColleagueEmail("");
      setExtraValues({});

      addReportToServer(user, {
        points: 0,
        reportTime: "00:00",
        answers: [selectedCategory.name, "badge"],
        reportIdRef,
      });

      await loadPosts();
      if (payload?.approval === "pending") {
        Swal.fire("Submitted", PENDING_APPROVAL_MESSAGE, "info");
      } else if (payload?.mailSent) {
        Swal.fire("Shared!", "Your badge has been posted and your colleague was notified.", "success");
      } else if (payload?.mailQueued) {
        Swal.fire(
          "Shared!",
          "Your badge has been posted. Your colleague will be notified by email shortly.",
          "success"
        );
      } else {
        const detail = payload?.mailError ? ` Reason: ${payload.mailError}` : "";
        Swal.fire(
          "Shared!",
          `Your badge has been posted, but the colleague email could not be sent.${detail}`,
          "warning"
        );
      }
    } catch (e) {
      Swal.fire("Network error", String(e.message || e), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async (gameId) => {
    if (!votingOpen || !user || !adminToken || likingId) return;

    // Liking is one-way: if the user already liked this post, just inform them
    // and never call the backend (so the count is preserved, not toggled off).
    const target = posts.find((p) => p.id === gameId);
    if (target?.likedByMe) {
      setShowAlreadyLiked(true);
      return;
    }

    setLikingId(gameId);
    try {
      const resp = await fetch(apiUrl("/yogaLike"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ userId: user.userId, gameId }),
      });
      const payload = await resp.json();
      if (resp.ok && payload?.success) {
        if (payload.liked) {
          setPosts((prev) =>
            prev.map((p) =>
              p.id === gameId ? { ...p, likedByMe: true, likeCount: payload.likeCount } : p
            )
          );
          setShowLikePopup(true);
        } else {
          // Backend toggled it off (already liked server-side) — keep it liked
          // in the UI and show the "already liked" message instead.
          setPosts((prev) =>
            prev.map((p) => (p.id === gameId ? { ...p, likedByMe: true } : p))
          );
          setShowAlreadyLiked(true);
        }
      }
    } catch (e) {
      console.error("yogaLike", e);
    } finally {
      setLikingId(null);
    }
  };

  const handleDelete = async (gameId) => {
    const r = await Swal.fire({
      title: "Delete this badge?",
      text: "This cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    });
    if (!r.isConfirmed) return;
    try {
      const resp = await fetch(apiUrl("/badgeDelete"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ userId: user.userId, gameId }),
      });
      const payload = await resp.json();
      if (resp.ok && payload?.success) {
        setPosts((prev) => prev.filter((p) => p.id !== gameId));
      } else {
        Swal.fire("Error", payload?.error || "Could not delete.", "error");
      }
    } catch (e) {
      Swal.fire("Network error", String(e.message || e), "error");
    }
  };

  const handleDownload = async (post) => {
    const node = cardRefs.current[post.id];
    if (!node) return;
    const imgs = Array.from(node.querySelectorAll("img[data-s3-file]"));
    const prev = imgs.map((img) => ({ img, src: img.getAttribute("src") }));
    try {
      // Temporarily load via same-origin proxy so html2canvas is not CORS-tainted.
      for (const { img } of prev) {
        const file = img.getAttribute("data-s3-file");
        if (file) {
          img.setAttribute("crossOrigin", "anonymous");
          img.setAttribute("src", badgeImageProxyUrl(file));
        }
      }
      await Promise.all(
        imgs.map(
          (img) =>
            img.complete
              ? Promise.resolve()
              : new Promise((resolve) => {
                  img.onload = () => resolve();
                  img.onerror = () => resolve();
                })
        )
      );
      const canvas = await html2canvas(node, { useCORS: true, backgroundColor: null, scale: 2 });
      const link = document.createElement("a");
      link.download = `badge-${post.category || "post"}-${post.id}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (e) {
      Swal.fire("Download failed", "Could not generate the image.", "error");
    } finally {
      for (const { img, src } of prev) {
        if (src != null) img.setAttribute("src", src);
        img.removeAttribute("crossOrigin");
      }
    }
  };

  if (status === "loading" || status === "idle") {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  if (status === "failed" || !themeData) {
    return (
      <div className="cg-page">
        <p style={{ textAlign: "center", color: titleColor }}>
          Could not load game data.{themeError ? ` (${themeError})` : ""}
        </p>
        <button
          type="button"
          className="cg-submit"
          style={{ margin: "16px auto", display: "block" }}
          onClick={() => dispatch(fetchThemeData({ themeId: null }))}
        >
          Retry
        </button>
      </div>
    );
  }

  const rules = Array.isArray(themeData?.rules) ? themeData.rules : [];

  return (
    <div className="cg-page">
      {mainEl && rules.length > 0
        ? createPortal(
            <button type="button" className="cg-rules-btn" style={btnStyle} onClick={() => setRulesOpen(true)}>
              How to play
            </button>,
            mainEl
          )
        : null}

      <h1 className="cg-title" style={{ color: titleColor }}>
        {themeData?.game_page_title || "Cheers to peers"}
      </h1>
      {themeData?.game_page_sub_title ? (
        <p className="cg-subtitle" style={{ color: titleColor }}>
          {formatGameSubtitle(themeData.game_page_sub_title)}
        </p>
      ) : null}

      {categories.length === 0 ? (
        <p className="cg-empty">No badges configured yet.</p>
      ) : (
        <>
          <div className="cg-carousel">
            <button
              type="button"
              className="cg-arrow"
              aria-label="Previous badges"
              onClick={() => goCarousel(-1)}
            >
              &#8592;
            </button>

            <div
              className="cg-carousel-track"
              ref={trackRef}
              onPointerDown={() => {
                carouselPauseUntilRef.current = Date.now() + 7000;
              }}
              onTouchStart={() => {
                carouselPauseUntilRef.current = Date.now() + 7000;
              }}
            >
              {categories.map((cat, i) => (
                <div
                  key={cat.key}
                  className={`cg-badge${i === selectedIndex ? " cg-badge--active" : ""}`}
                  onClick={() => setSelectedIndex(i)}
                >
                  <div className="cg-badge-img-wrap">
                    {cat.icon ? (
                      <img src={mediaUrl(cat.icon)} alt={cat.name} referrerPolicy="no-referrer" />
                    ) : (
                      <div className="cg-badge-noimg">
                        <i className="fa-solid fa-image" />
                      </div>
                    )}
                    <button
                      type="button"
                      className="cg-zoom"
                      aria-label={`Zoom ${cat.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setZoomCat(cat);
                      }}
                    >
                      <i className="fa-solid fa-magnifying-glass-plus" />
                    </button>
                  </div>
                  <span className="cg-badge-name">{cat.name}</span>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="cg-arrow"
              aria-label="Next badges"
              onClick={() => goCarousel(1)}
            >
              &#8594;
            </button>
          </div>

          <div
            className="cg-form"
            style={{ "--cg-field-bg": likeBoxBg, "--cg-field-text": likeBoxText }}
          >
            <div className="cg-form-row">
              <label className="cg-label">{labels.name}:</label>
              <input className="cg-input" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="cg-form-row">
              <label className="cg-label">{labels.colleagueName}:</label>
              <input
                className="cg-input"
                value={colleagueName}
                placeholder={labels.colleagueName}
                onChange={(e) => setColleagueName(e.target.value)}
              />
            </div>
            <div className="cg-form-row">
              <label className="cg-label">{labels.colleagueEmail}:</label>
              <input
                className="cg-input"
                type="email"
                value={colleagueEmail}
                placeholder={labels.colleagueEmail}
                onChange={(e) => setColleagueEmail(e.target.value)}
              />
            </div>
            <div className="cg-form-row">
              <label className="cg-label">{labels.badge}:</label>
              <select
                className="cg-input"
                value={selectedIndex}
                onChange={(e) => setSelectedIndex(Number(e.target.value))}
              >
                {categories.map((cat, i) => (
                  <option key={cat.key} value={i}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {extraFieldDefs.map((f) => {
              const fieldLabel =
                f.label ||
                String(f.name || "")
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (c) => c.toUpperCase());
              return (
              <div className="cg-form-row" key={f.name}>
                <label className="cg-label">{fieldLabel}:</label>
                <input
                  className="cg-input"
                  type={f.type === "email" ? "email" : "text"}
                  value={extraValues[f.name] ?? ""}
                  placeholder={fieldLabel}
                  onChange={(e) =>
                    setExtraValues((prev) => ({ ...prev, [f.name]: e.target.value }))
                  }
                />
              </div>
              );
            })}

            <div className="cg-form-row cg-form-row--msg">
              <label className="cg-label">Write a Message:</label>
              <div className="cg-msg-wrap">
                <textarea
                  className="cg-input cg-textarea"
                  rows={4}
                  maxLength={MESSAGE_MAX_LEN}
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX_LEN))}
                />
                <div className="cg-charcount">
                  {message.length} / {MESSAGE_MAX_LEN} characters allowed
                </div>
              </div>
            </div>

            <button
              type="button"
              className="cg-submit"
              style={btnStyle}
              onClick={handleSubmit}
              disabled={submitting || !badgeSharingOn}
            >
              {submitting ? "Sharing…" : "SUBMIT"}
            </button>
            <p className="cg-like-note">You can LIKE any post only once</p>
          </div>
        </>
      )}

      <div className="cg-posts">
        {!loadingPosts && posts.length > 0 && filterOptions.length > 1 ? (
          <div className="cg-filter-toolbar">
            <label className="cg-filter-label" htmlFor="cg-filter-select">
              Filter by badge:
            </label>
            <select
              id="cg-filter-select"
              className="cg-filter-select"
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
            >
              {filterOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {loadingPosts ? (
          <div className="quiz-loader-container">
            <div className="quiz-loader" />
          </div>
        ) : filteredPosts.length === 0 ? (
          <p className="cg-no-posts" style={{ backgroundColor: likeBoxBg, color: likeBoxText }}>
            There is no post in this Category
          </p>
        ) : (
          <>
          <div className="cg-posts-grid">
            {pagedPosts.map((post) => (
              <article
                className="cg-post-card"
                key={post.id}
                ref={(el) => {
                  cardRefs.current[post.id] = el;
                }}
              >
                <div className="cg-post-img">
                  {post.icon ? (
                    <img
                      src={mediaUrl(post.icon)}
                      alt={post.category}
                      data-s3-file={post.icon || undefined}
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="cg-badge-noimg">
                      <i className="fa-solid fa-image" />
                    </div>
                  )}
                  {post.mine ? (
                    <button
                      type="button"
                      className="cg-post-delete"
                      data-html2canvas-ignore="true"
                      aria-label="Delete badge"
                      onClick={() => handleDelete(post.id)}
                    >
                      &times;
                    </button>
                  ) : null}
                </div>

                <div className="cg-post-body" style={{ backgroundColor: likeBoxBg, color: likeBoxText }}>
                  <div className="cg-post-meta">
                    <button
                      type="button"
                      className={`cg-like${post.likedByMe ? " cg-like--on" : ""}`}
                      style={{ color: likeBoxText }}
                      onClick={() => handleLike(post.id)}
                      disabled={!votingOpen || likingId === post.id}
                      aria-label={votingOpen ? "Like" : "Likes are disabled"}
                      title={votingOpen ? "Like" : "Voting is closed"}
                    >
                      <i className="fa-solid fa-thumbs-up" /> <span>{post.likeCount}</span>
                    </button>
                    <span className="cg-post-from">From {post.name || "Anonymous"}</span>
                  </div>
                  <div className="cg-post-category">{post.category}</div>
                  {post.message ? <div className="cg-post-message">{post.message}</div> : null}
                  <div className="cg-post-footer">
                    <button
                      type="button"
                      className="cg-download"
                      style={{ color: likeBoxText }}
                      onClick={() => handleDownload(post)}
                      aria-label="Download"
                    >
                      <i className="fa-solid fa-download" />
                    </button>
                    {post.colleagueName ? (
                      <span className="cg-post-to">To {post.colleagueName}</span>
                    ) : null}
                  </div>
                </div>
              </article>
            ))}
          </div>

          {totalPages > 1 ? (
            <div className="cg-pagination">
              <button
                type="button"
                className="cg-page-btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button
                  type="button"
                  key={n}
                  className={`cg-page-num${n === page ? " cg-page-num--active" : ""}`}
                  style={n === page ? { backgroundColor: likeBoxBg, color: likeBoxText, borderColor: likeBoxBg } : undefined}
                  onClick={() => setPage(n)}
                >
                  {n}
                </button>
              ))}
              <button
                type="button"
                className="cg-page-btn"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          ) : null}
          </>
        )}
      </div>

      {zoomCat && (
        <div className="cg-modal-backdrop" onClick={() => setZoomCat(null)}>
          <div className="cg-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="cg-modal-close" onClick={() => setZoomCat(null)}>
              &times;
            </button>
            {zoomCat.icon ? (
              <img src={mediaUrl(zoomCat.icon)} alt={zoomCat.name} referrerPolicy="no-referrer" />
            ) : null}
            <h3>{zoomCat.name}</h3>
            {descOnHover && zoomCat.description ? <p>{zoomCat.description}</p> : null}
          </div>
        </div>
      )}

      {rulesOpen ? (
        <RulesModal
          open
          onClose={() => setRulesOpen(false)}
          rules={rules}
          btnBg={btnBg}
          btnText={btnText}
          title="How to play"
          titleId="cg-rules-title"
        />
      ) : null}

      {showLikePopup && (
        <div className="cg-modal-backdrop" onClick={() => setShowLikePopup(false)}>
          <div className="cg-like-popup" onClick={(e) => e.stopPropagation()}>
            <img className="cg-like-popup-thumb" src={likeThumb} alt="Thumbs up" />
            <div className="cg-like-popup-text">Thank you for liking!</div>
            <button type="button" className="cg-like-popup-ok" onClick={() => setShowLikePopup(false)}>
              OK
            </button>
          </div>
        </div>
      )}

      {showAlreadyLiked && (
        <div className="cg-modal-backdrop" onClick={() => setShowAlreadyLiked(false)}>
          <div className="cg-like-popup" onClick={(e) => e.stopPropagation()}>
            <img className="cg-ohno-img" src={ohnoImg} alt="Already liked" />
            <button type="button" className="cg-like-popup-ok" onClick={() => setShowAlreadyLiked(false)}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
